import * as React from 'react'
import styled from 'styled-components'

import * as entity from '@boutique/entities'
import { usePageView } from '@boutique/shadow'
import * as utils from '@boutique/utils'
import * as components from '@bob/components'
import * as ds from '@bob/design-system'
import * as hooks from '@bob/hooks'
import * as url from '@bob/url'

import { Brands } from './Brands'
import { Seo } from './Seo'

export type ArticleListProps = {
  articles: entity.article.MediumArticle[]
  brands: entity.brand.ColdBrand[]
  index: hooks.useSearchEngine.Index
}

const DEFAULT_PAGE_SIZE = 9

export function ArticleList(props: ArticleListProps): React.ReactElement {
  return (
    <>
      <Seo />
      <View {...props} />
    </>
  )
}

function View({ articles, brands, index }: ArticleListProps) {
  const pageUrl = url.articles()

  const engine = hooks.useSearchEngine.useSearchEngine(articles, {
    index,
    identifier: (article) => article.slug,
    fullText: (query, article) => {
      const normalizedQuery = utils.text.slugify(query)
      return (
        utils.text.slugify(article.title).includes(normalizedQuery) ||
        utils.text.slugify(article.description).includes(normalizedQuery) ||
        utils.text.slugify(article.category.name).includes(normalizedQuery) ||
        article.tags.some((tag) => utils.text.slugify(tag.name).includes(normalizedQuery))
      )
    },
    defaultQuery: {
      filters: {},
      text: '',
      sortType: hooks.useSearchEngine.SortType.new,
    },
    sorts: [
      {
        type: hooks.useSearchEngine.SortType.new,
        name: 'Nouveautés',
        getDate: (article) => {
          return new Date(article.forceUpdatedAt ? article.forceUpdatedAt : article.firstPublishedAt ?? '')
        },
      },
      {
        type: hooks.useSearchEngine.SortType.AtoZ,
        name: 'Nom (A à Z)',
        getTitle: (article) => {
          return article.title
        },
      },
      {
        type: hooks.useSearchEngine.SortType.ZtoA,
        name: 'Nom (Z à A)',
        getTitle: (article) => {
          return article.title
        },
      },
    ],
    defaultPage: 1,
    defaultSize: DEFAULT_PAGE_SIZE,
  })

  usePageView(Object.keys(index))

  return (
    <>
      <ds.Container>
        <ds.BreadCrumbs crumbs={pageUrl.crumbs} />

        <components.ListingLayout.Heading
          color={ds.color.YELLOW}
          description="Retrouvez nos articles, conseils et portraits, 100% faits maison."
          title={'Magazine'}
        />

        <components.ListingLayout.DesktopContainer>
          <components.Sort engine={engine} label="articles triés par " />
        </components.ListingLayout.DesktopContainer>

        <Wrapper>
          <components.ListingLayout.ScrollAnchor id="top" />
          <components.ListingLayout.Aside>
            <components.Search engine={engine} placeholder="Rechercher un article" />
            <components.ListingLayout.Controls>
              <components.Filters engine={engine} />
              <components.ListingLayout.MobileContainer>
                <components.Sort engine={engine} label="articles triés par " />
              </components.ListingLayout.MobileContainer>
            </components.ListingLayout.Controls>
          </components.ListingLayout.Aside>

          <components.ListingLayout.Main>
            <components.ResultList engine={engine} loading={placeholders}>
              {({ item: article }) => {
                return (
                  <components.ListingLayout.Item key={article.slug}>
                    <ds.ArticleCard
                      href={
                        url.article({
                          slug: article.slug,
                          title: article.title,
                        }).path
                      }
                      date={new Date(
                        article.forceUpdatedAt ? article.forceUpdatedAt : article.firstPublishedAt ?? '',
                      ).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                      })}
                      image={{
                        ...article.image,
                        nativeLazyLoad: true,
                      }}
                      tag={article.section}
                      title={article.title}
                    />
                  </components.ListingLayout.Item>
                )
              }}
            </components.ResultList>

            <components.ListingLayout.Pagination>
              <components.Pagination engine={engine} />
            </components.ListingLayout.Pagination>
          </components.ListingLayout.Main>
        </Wrapper>
        <Brands brands={brands} />
      </ds.Container>
    </>
  )
}

const placeholders = [...new Array(DEFAULT_PAGE_SIZE).keys()].map((index) => (
  <components.ListingLayout.Item key={`article-skeleton-${index}`}>
    <ds.ArticleCardSkeleton />
  </components.ListingLayout.Item>
))

export const Wrapper = styled(components.ListingLayout.Wrapper)`
  margin-top: 20px;

  @media screen and (${ds.breakpoint.TABLET}) {
    margin-top: 0px;
  }
`
